import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const languages = [
    { code: 'fa', name: 'FA', fullName: 'فارسی', flag: '🇮🇷' },
    { code: 'en', name: 'EN', fullName: 'English', flag: '🇺🇸' },
    { code: 'ru', name: 'RU', fullName: 'Русский', flag: '🇷🇺' },
    { code: 'ar', name: 'AR', fullName: 'العربية', flag: '🇸🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const menuItems = [
    { 
      name: t('nav.home'), 
      path: '/', 
      icon: 'ri-home-4-line',
      hasDropdown: false
    },
    { 
      name: t('nav.about'), 
      path: '/about', 
      icon: 'ri-information-line',
      hasDropdown: false
    },
    { 
      name: t('nav.services'), 
      path: '/services', 
      icon: 'ri-service-line',
      hasDropdown: true,
      dropdownItems: [
        { name: 'طراحی گلخانه', path: '/services#design', icon: 'ri-building-line' },
        { name: 'گلخانه هوشمند', path: '/services#smart', icon: 'ri-settings-3-line' },
        { name: 'مشاوره تخصصی', path: '/services#consulting', icon: 'ri-customer-service-2-line' },
        { name: 'نگهداری و تعمیر', path: '/services#maintenance', icon: 'ri-tools-line' }
      ]
    },
    { 
      name: t('nav.products'), 
      path: '/products', 
      icon: 'ri-product-hunt-line',
      hasDropdown: true,
      dropdownItems: [
        { name: 'کوکوپیت', path: '/products/2', icon: 'ri-seedling-line' },
        { name: 'نخ یووی دار', path: '/products/3', icon: 'ri-thread-line' },
        { name: 'توری ضد تریپس', path: '/products/4', icon: 'ri-grid-line' },
        { name: 'چسب ترمیم پلاستیک', path: '/products/5', icon: 'ri-sticky-note-line' },
        { name: 'مشاهده همه', path: '/products', icon: 'ri-eye-line' }
      ]
    },
    { 
      name: t('nav.projects'), 
      path: '/projects', 
      icon: 'ri-building-line',
      hasDropdown: false
    },
    { 
      name: t('nav.partners'), 
      path: '/partners', 
      icon: 'ri-team-line',
      hasDropdown: false
    },
    { 
      name: t('nav.news'), 
      path: '/news', 
      icon: 'ri-newspaper-line',
      hasDropdown: false
    },
    { 
      name: t('nav.contact'), 
      path: '/contact', 
      icon: 'ri-phone-line',
      hasDropdown: false
    }
  ];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when clicking outside or route changes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-dropdown') && !target.closest('.language-button')) {
        setIsLanguageOpen(false);
      }
      if (!target.closest('.mobile-menu') && !target.closest('.mobile-menu-button')) {
        setIsMenuOpen(false);
      }
      if (!target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsLanguageOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleDropdownToggle = (itemName: string) => {
    setOpenDropdown(openDropdown === itemName ? null : itemName);
  };

  const handleDropdownEnter = (itemName: string) => {
    setOpenDropdown(itemName);
  };

  const handleDropdownLeave = () => {
    // Add a small delay to prevent flickering when moving between elements
    setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 w-full transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/99 backdrop-blur-xl shadow-2xl border-b border-emerald-100/50' 
          : 'bg-white/99 backdrop-blur-md shadow-xl border-b border-gray-100/30'
      }`}
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%'
      }}
    >
      <nav 
        className="w-full px-4 sm:px-6 lg:px-8" 
        style={{ 
          position: 'relative', 
          zIndex: 99999,
          width: '100%'
        }}
      >
        <div 
          className="flex items-center justify-between h-20 w-full" 
          style={{ 
            position: 'relative', 
            zIndex: 99999,
            width: '100%',
            maxWidth: 'none'
          }}
        >
          
          {/* Logo Section */}
          <div className="flex items-center space-x-4 min-w-0 flex-shrink-0">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-4 group min-w-0 flex-shrink-0" 
              style={{ 
                position: 'relative', 
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl border-2 border-emerald-200/30 shadow-xl group-hover:shadow-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                  <img 
                    src="https://static.readdy.ai/image/446882068b23debf400f3bc55bfe3906/15a60f8ad8230155d9447ef33246db53.png" 
                    alt="Aria Green Greenhouse Logo" 
                    className="w-12 h-12 object-contain transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse shadow-lg"></div>
                <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-bounce delay-300"></div>
              </div>
              <div 
                className="hidden sm:block flex-shrink-0"
                style={{ 
                  position: 'relative', 
                  zIndex: 99999,
                  display: 'block'
                }}
              >
                <h1 
                  className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-emerald-700 via-teal-600 to-cyan-600 bg-clip-text text-transparent group-hover:from-emerald-800 group-hover:to-teal-700 transition-all duration-500 whitespace-nowrap" 
                  style={{ 
                    fontFamily: 'Pacifico, serif',
                    position: 'relative',
                    zIndex: 99999
                  }}
                >
                  {t('company.name')}
                </h1>
                <p 
                  className="text-xs lg:text-sm text-gray-600 font-medium group-hover:text-emerald-600 transition-colors duration-300 whitespace-nowrap"
                  style={{ 
                    position: 'relative',
                    zIndex: 99999
                  }}
                >
                  {t('company.tagline')}
                </p>
              </div>
            </Link>
          </div>

          {/* Center Menu Button */}
          <div 
            className="flex-1 flex items-center justify-center relative mobile-menu-container"
            style={{ 
              position: 'relative', 
              zIndex: 99999 
            }}
          >
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              onMouseEnter={() => setIsMenuOpen(true)}
              className="mobile-menu-button w-10 h-10 flex items-center justify-center cursor-pointer rounded-full hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 transition-all duration-300 border border-gray-200/50 hover:border-emerald-200 hover:shadow-md bg-white/80 backdrop-blur-sm group"
              style={{ 
                position: 'relative', 
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div className="relative w-5 h-5 flex items-center justify-center">
                <span className={`absolute w-4 h-0.5 bg-gray-700 group-hover:bg-emerald-600 transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-1.5'}`}></span>
                <span className={`absolute w-4 h-0.5 bg-gray-700 group-hover:bg-emerald-600 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`absolute w-4 h-0.5 bg-gray-700 group-hover:bg-emerald-600 transition-all duration-300 ${isMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-1.5'}`}></span>
              </div>
            </button>

            {/* Mobile Vertical Menu */}
            <div 
              className={`absolute left-1/2 transform -translate-x-1/2 mt-3 w-64 bg-white/99 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
                isMenuOpen 
                  ? 'opacity-100 visible translate-y-0 pointer-events-auto' 
                  : 'opacity-0 invisible -translate-y-2 pointer-events-none'
              }`}
              style={{ 
                zIndex: 999999,
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: '12px'
              }}
              onMouseEnter={() => setIsMenuOpen(true)}
              onMouseLeave={() => setIsMenuOpen(false)}
            >
              <div className="p-4">
                <div className="flex flex-col space-y-2">
                  {menuItems.map((item, index) => (
                    <div key={item.path} className="dropdown-container">
                      {item.hasDropdown ? (
                        <div>
                          <Link
                            to={item.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setOpenDropdown(null);
                            }}
                            className={`w-full flex items-center justify-between py-3 px-4 cursor-pointer rounded-xl transition-all duration-300 group ${
                              isActivePath(item.path)
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                                : 'text-gray-700 hover:text-emerald-600 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50'
                            }`}
                            style={{
                              transitionDelay: `${index * 50}ms`
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                                isActivePath(item.path)
                                  ? 'bg-white/20'
                                  : 'bg-gradient-to-r from-emerald-100 to-teal-100 group-hover:from-emerald-200 group-hover:to-teal-200'
                              }`}>
                                <i className={`${item.icon} text-lg transition-transform duration-300 group-hover:scale-110`}></i>
                              </div>
                              <span className="font-medium">{item.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDropdownToggle(item.name);
                              }}
                              className="cursor-pointer"
                            >
                              <i className={`ri-arrow-down-s-line transition-transform duration-300 ${openDropdown === item.name ? 'rotate-180' : ''}`}></i>
                            </button>
                          </Link>
                          
                          {/* Mobile Dropdown */}
                          <div className={`mt-2 mr-4 space-y-1 transition-all duration-300 overflow-hidden ${
                            openDropdown === item.name 
                              ? 'max-h-96 opacity-100' 
                              : 'max-h-0 opacity-0'
                          }`}>
                            {item.dropdownItems?.map((dropdownItem, dropIndex) => (
                              <Link
                                key={dropIndex}
                                to={dropdownItem.path}
                                className="flex items-center space-x-3 py-2 px-4 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-300 cursor-pointer"
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setOpenDropdown(null);
                                }}
                              >
                                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg">
                                  <i className={`${dropdownItem.icon} text-emerald-600 text-sm`}></i>
                                </div>
                                <span className="text-sm font-medium">{dropdownItem.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          className={`flex items-center space-x-3 py-3 px-4 cursor-pointer rounded-xl transition-all duration-300 group ${
                            isActivePath(item.path)
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                              : 'text-gray-700 hover:text-emerald-600 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                          style={{
                            transitionDelay: `${index * 50}ms`
                          }}
                        >
                          <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                            isActivePath(item.path)
                              ? 'bg-white/20'
                              : 'bg-gradient-to-r from-emerald-100 to-teal-100 group-hover:from-emerald-200 group-hover:to-teal-200'
                          }`}>
                            <i className={`${item.icon} text-lg transition-transform duration-300 group-hover:scale-110`}></i>
                          </div>
                          <span className="font-medium">{item.name}</span>
                          {isActivePath(item.path) && (
                            <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          )}
                        </Link>
                      )}
                    </div>
                  ))}
                  
                  {/* Mobile CTA */}
                  <Link
                    to="/contact"
                    className="flex items-center justify-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer mt-4 group relative"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="ri-phone-line text-lg group-hover:animate-pulse"></i>
                    <span>{t('nav.contact')}</span>
                    <div className="absolute inset-0 bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - CTA & Language */}
          <div 
            className="flex items-center space-x-3 flex-shrink-0" 
            style={{ 
              position: 'relative', 
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            
            {/* CTA Button - Enhanced */}
            <Link
              to="/contact"
              className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2.5 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer group text-sm relative"
              style={{ 
                position: 'relative', 
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <i className="ri-phone-line text-sm group-hover:animate-pulse"></i>
              <span className="text-sm">{t('nav.contact')}</span>
              <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>

            {/* Language Selector - Enhanced */}
            <div 
              className="relative language-dropdown" 
              style={{ 
                position: 'relative', 
                zIndex: 99999 
              }}
            >
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="language-button flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-emerald-600 cursor-pointer rounded-full hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 transition-all duration-300 border border-gray-200/50 hover:border-emerald-200 hover:shadow-md bg-white/80 backdrop-blur-sm"
                style={{ 
                  position: 'relative', 
                  zIndex: 99999,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <span className="text-base">{currentLanguage.flag}</span>
                <span className="text-sm font-medium hidden sm:block">{currentLanguage.name}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLanguageOpen && (
                <div 
                  className="absolute right-0 mt-3 w-44 bg-white/99 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-top-2 duration-300" 
                  style={{ 
                    zIndex: 999999, 
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '12px'
                  }}
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-all duration-300 cursor-pointer ${
                        lang.code === i18n.language
                          ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-600 border-l-4 border-emerald-500'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 hover:text-emerald-600'
                      }`}
                      style={{ 
                        position: 'relative', 
                        zIndex: 999999,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{lang.name}</div>
                        <div className="text-xs text-gray-500">{lang.fullName}</div>
                      </div>
                      {lang.code === i18n.language && (
                        <i className="ri-check-line text-emerald-500"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
